import { get } from "@/services/axiosUitils";
import {
  ACTIVITY_API_CONFIG,
  getActivityEndpoints,
} from "../config/activityConfig";


// ========================================
// Types & Interfaces
// ========================================

export interface ApiActivityResponse {
  total_steps?: number;
  active_zone_minutes?: number;
  heart_rate?: number;
  calories_burned?: number;
  distance_covered?: number;
  sleep_tracking?: number;
  total_floors?: number;
  "oxygen_saturation(spo2)"?: number;
  breathing_rate?: number;
  skin_temperature_variation?: number;
  weight?: number;
  body_fat_percentage?: number;
  bmi?: number;
  "heart_rate_variability(hrv)"?: number;
  stress?: string;
  systolic_blood_pressure?: number;
  diastolic_blood_pressure?: number;
}

export interface ActivityData {
  date: string;
  steps: number;
  distance: string;
  calories: string;
  activeTime: string;
  heartRate: string;
  bloodPressure: {
    systolic: string;
    diastolic: string;
  };
  oxygenSaturation: string;
  breathingRate: string;
  skinTemp: string;
  weight: string;
  bodyFat: string;
  bmi: string;
  hrv: string;
  stress: string;
}

export interface ActivityResponse {
  success: boolean;
  data: ActivityData;
  message?: string;
}

export interface TransformedActivityData {
  date: string;
  steps: string;
  target: string;
  percentage: string;
  activityTime: string;
  heartRate: string;
  calories: string;
  distance: string;
  sleepHours: string;
  floors: string;
  oxygenSaturation: string;
  breathingRate: string;
  skinTemp: string;
  weight: string;
  bodyFat: string;
  bmi: string;
  hrv: string;
  stress: string;
}

interface CachedActivity {
  data: ActivityData;
  timestamp: number;
}

// ========================================
// Activity Service Class
// ========================================

class ActivityService {
  // Cache configuration
  private cache = new Map<string, CachedActivity>();
  private readonly CACHE_EXPIRY_TIME = ACTIVITY_API_CONFIG.CACHE_DURATION;
  private readonly MAX_CACHE_SIZE = 50;

  /**
   * Get cached data if available and not expired
   */
  private getCachedData(date: string): ActivityData | null {
    const cached = this.cache.get(date);
    if (!cached) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - cached.timestamp > this.CACHE_EXPIRY_TIME;

    if (isExpired) {
      this.cache.delete(date);
      return null;
    }

    return cached.data;
  }

  /**
   * Set cached data with LRU eviction
   */
  private setCachedData(date: string, data: ActivityData): void {
    // Manage cache size (LRU eviction)
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(date, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Create default activity data with zero values
   */
  private createDefaultActivityData(date: string): ActivityData {
    return {
      date: date,
      steps: 0,
      distance: "0.0 km",
      calories: "0",
      activeTime: "0 min",
      heartRate: "0 bpm",
      bloodPressure: {
        systolic: "0",
        diastolic: "0",
      },
      oxygenSaturation: "0%",
      breathingRate: "0 bpm",
      skinTemp: "0°C",
      weight: "0 kg",
      bodyFat: "0%",
      bmi: "0",
      hrv: "0 ms",
      stress: ACTIVITY_API_CONFIG.DEFAULTS.STRESS_LEVEL,
    };
  }

  /**
   * Fetch activity data for specific date with caching support
   * Uses common axios utility - Token & Tenant-ID auto-injected!
   *
   * @param date Date string in YYYY-MM-DD format
   * @returns Activity data for the specified date
   */
  getActivityData = async (date: string): Promise<ActivityData> => {
    // Check cache first
    const cachedData = this.getCachedData(date);
    if (cachedData) {
      return cachedData;
    }

    try {
      // Try different endpoint variations
      const endpoints = getActivityEndpoints();
      let lastError: any = null;

      for (const endpoint of endpoints) {
        try {
          // ✅ Using common 'get' utility - daily API no longer requires fitbit-auth header
          // Token, Tenant-ID automatically added by axiosConfig interceptor!
          const response = await get<any>(endpoint, {
            params: { date },
          });

          // Handle different response structures
          let activityData: ActivityData;

          // Case 1: Direct data object
          if (response && typeof response === "object" && response.date) {
            activityData = response;
          }
          // Case 2: Wrapped in { success: true, data: {...} }
          else if (response?.data) {
            activityData = response.data;
          }
          // Case 3: Response is the data itself
          else {
            activityData = response;
          }

          // Cache successful response
          this.setCachedData(date, activityData);
          return activityData;
        } catch (err: any) {
          lastError = err;
          // If 404, try next endpoint
          if (err.response?.status === 404) {
            continue;
          }
          // If other error, throw immediately
          throw err;
        }
      }

      // If all endpoints return 404, return default data
      if (lastError?.response?.status === 404) {
        const defaultData = this.createDefaultActivityData(date);
        this.setCachedData(date, defaultData);
        return defaultData;
      }

      throw lastError;
    } catch (_error: any) {
      // Error already handled by axiosUitils handelError()
      // Return default data for graceful degradation
      const defaultData = this.createDefaultActivityData(date);
      this.setCachedData(date, defaultData);
      return defaultData;
    }
  };

  /**
   * Fetch activity data for today
   */
  getTodayActivity = async (): Promise<ActivityData> => {
    const today = new Date().toISOString().split("T")[0];
    return this.getActivityData(today);
  };

  /**
   * Fetch activity data for yesterday
   */
  getYesterdayActivity = async (): Promise<ActivityData> => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split("T")[0];
    return this.getActivityData(yesterdayStr);
  };

  /**
   * Fetch activity data for multiple days
   * @param days Number of days to fetch (default: 7)
   */
  getActivityDataRange = async (days: number = 7): Promise<ActivityData[]> => {
    const promises: Promise<ActivityData>[] = [];
    const today = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];
      promises.push(this.getActivityData(dateStr));
    }

    const results = await Promise.allSettled(promises);

    return results
      .filter(
        (result): result is PromiseFulfilledResult<ActivityData> =>
          result.status === "fulfilled"
      )
      .map(result => result.value);
  };

  /**
   * Transform API data to component display format
   */
  transformActivityData = (
    apiData: ApiActivityResponse
  ): TransformedActivityData => {
    const steps = Number(apiData?.total_steps) || 0;
    const target = ACTIVITY_API_CONFIG.DEFAULTS.STEPS_TARGET;
    const percentage = Math.round((steps / target) * 100);

    return {
      date: new Date().toISOString().split("T")[0],
      steps: steps.toLocaleString(),
      target: target.toLocaleString(),
      percentage: `${percentage}%`,
      activityTime: `${apiData?.active_zone_minutes ?? 0} min`,
      heartRate: `${apiData?.heart_rate ?? 0}`,
      calories: (Number(apiData?.calories_burned) || 0).toLocaleString(),
      distance: ((apiData.distance_covered ?? 0) / 1000).toFixed(1),
      sleepHours: (apiData.sleep_tracking ?? 0).toFixed(1),
      floors: (apiData.total_floors ?? 0).toString(),
      oxygenSaturation: `${apiData["oxygen_saturation(spo2)"] ?? 0}%`,
      breathingRate: `${apiData.breathing_rate ?? 0} bpm`,
      skinTemp: `${apiData.skin_temperature_variation ?? 0}°C`,
      weight: `${apiData.weight ?? 0} kg`,
      bodyFat: `${apiData.body_fat_percentage ?? 0}%`,
      bmi: `${apiData.bmi ?? 0}`,
      hrv: `${apiData["heart_rate_variability(hrv)"] ?? 0} ms`,
      stress: apiData.stress ?? ACTIVITY_API_CONFIG.DEFAULTS.STRESS_LEVEL,
    };
  };

  /**
   * Clear all cached data
   */
  clearCache = (): void => {
    this.cache.clear();
  };

  /**
   * Clear cached data for a specific date
   */
  clearCacheForDate = (date: string): void => {
    this.cache.delete(date);
  };

  /**
   * Get cache statistics for debugging
   */
  getCacheStats = (): { size: number; keys: string[] } => {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  };

  /**
   * Format date for API (YYYY-MM-DD)
   */
  formatDateForAPI = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  /**
   * Format date for display
   */
  formatDateForDisplay = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return (
        "Today, " +
        date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      );
    } else if (date.toDateString() === yesterday.toDateString()) {
      return (
        "Yesterday, " +
        date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      );
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return (
        "Tomorrow, " +
        date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      );
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
      });
    }
  };
}

// Export singleton instance (City Collab pattern)
const activityService = new ActivityService();
export default activityService;
